import type { Pin } from "@agent-native/pinpoint";
import type { ElementCapture } from "./elementCapture";

interface ResolvedSource {
  fileName: string;
  lineNumber?: number;
}

/** Injected resolvers — real impl is bippy-backed (see createBippyPinEnricher). */
export interface PinSourceDeps {
  /** Re-find the pinned element in the live DOM (by domPath, then selector). */
  findElement: (pin: Pin) => Element | null;
  /** The host fiber for the element, followed by its ancestor component fibers
   *  (nearest first) — bippy walks element → fiber → up the `.return` chain. */
  getFibers: (element: Element) => unknown[];
  /** bippy/source: fiber → {file, line} via _debugStack + source maps. */
  getSource: (fiber: unknown) => Promise<ResolvedSource | null>;
  /** bippy/source: fiber → real (un-minified) component name. */
  getName: (fiber: unknown) => Promise<string | null>;
}

/** Strip bundler scheme prefixes so a stored path is clean & repo-relative. */
export function normalizeSourcePath(fileName: string): string {
  return fileName
    .replace(/^[a-z-]+:\/\/[^/]*\//i, "") // turbopack:///, webpack://host/, file://…
    .replace(/^\[project\]\//, "") // turbopack [project] marker
    .replace(/^\.\//, ""); // leading ./
}

/**
 * How useful a resolved path is as a triage anchor (lower = better):
 *  0 feature   — app code that identifies WHERE (features/…, shared/ui/…)
 *  1 primitive — the shared design-system layer (components/ui/*): a shadcn/Base
 *                UI wrapper reused everywhere, so a poor "where" anchor
 *  2 vendor    — node_modules (a bare library primitive)
 */
function sourceRank(path: string): 0 | 1 | 2 {
  if (path.includes("node_modules")) return 2;
  if (/(^|\/)components\/ui\//.test(path)) return 1;
  return 0;
}

/** A real React component name (PascalCase, ≥2 chars) — not a minified token
 *  like "M"/"$"/"l" that prod builds leave on `fiber.type.name`. */
function isRealComponentName(name: string | null): name is string {
  return !!name && /^[A-Z][A-Za-z0-9]/.test(name);
}

/** Best-effort component name from a file path: column-header.tsx → ColumnHeader. */
export function componentNameFromPath(path: string): string | null {
  const base = path.split("/").pop()?.replace(/\.[jt]sx?$/, "");
  if (!base) return null;
  const name = base
    .split(/[-_.]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
  return name || null;
}

/**
 * Fill each pin's `framework.sourceFile` (`path:line`) and un-minify its
 * `componentPath`, resolved from the live DOM at send time.
 *
 * Why here and not from pinpoint: pinpoint 0.1.10 only reads `fiber._debugSource`
 * (dev-only, and removed in React 19), so on kvota's Next 16 + React 19 prod its
 * own resolver always yields null. bippy's source module walks React 19's
 * `_debugStack` and symbolicates against the browser source maps we ship
 * (`productionBrowserSourceMaps: true`), which works in prod. See memory
 * project_pinpoint_source_attribution_react19_ceiling.
 *
 * Resolution prefers the host element's own JSX site; if that lands in
 * node_modules (a bare library primitive like a Base UI `<button>`) it climbs
 * to the nearest ancestor component whose source is app code.
 */
export async function enrichPinsWithSource(
  pins: Pin[],
  deps: PinSourceDeps,
): Promise<Pin[]> {
  return Promise.all(pins.map((pin) => enrichPin(pin, deps)));
}

async function enrichPin(pin: Pin, deps: PinSourceDeps): Promise<Pin> {
  // Dev builds already carry a real sourceFile — never re-resolve.
  if (pin.framework?.sourceFile) return pin;

  try {
    const element = deps.findElement(pin);
    if (!element) return pin;

    const fibers = deps.getFibers(element);
    if (fibers.length === 0) return pin;

    // Climb the fiber chain and keep the best-ranked resolvable source: a
    // feature file beats the shared design-system wrapper beats node_modules.
    let best: { fiber: unknown; source: ResolvedSource; rank: 0 | 1 | 2 } | null = null;
    for (const fiber of fibers) {
      const source = await deps.getSource(fiber);
      if (!source) continue;
      const rank = sourceRank(normalizeSourcePath(source.fileName));
      if (best === null || rank < best.rank) best = { fiber, source, rank };
      if (rank === 0) break; // feature-level is the best possible — stop early
    }
    return best ? applySource(pin, best.fiber, best.source, deps) : pin;
  } catch {
    // Source attribution is best-effort enrichment — a resolver failure must
    // never drop the pin from the report.
    return pin;
  }
}

async function applySource(
  pin: Pin,
  fiber: unknown,
  source: ResolvedSource,
  deps: PinSourceDeps,
): Promise<Pin> {
  const path = normalizeSourcePath(source.fileName);
  // Prefer bippy's resolved name only when it's a real PascalCase name — in prod
  // it often returns the minified runtime token ("M", "$"), so fall back to the
  // file basename (column-header.tsx → ColumnHeader) rather than shipping "<M>".
  const resolved = await deps.getName(fiber);
  const name = isRealComponentName(resolved) ? resolved : componentNameFromPath(path);
  const framework = {
    framework: "react",
    componentPath: "",
    ...pin.framework,
  };
  framework.sourceFile = source.lineNumber ? `${path}:${source.lineNumber}` : path;
  if (name) framework.componentPath = `<${name}>`;
  return { ...pin, framework };
}

/** An enricher bound to the real bippy resolvers (client-only, lazy imports). */
export type PinEnricher = (pins: Pin[]) => Promise<Pin[]>;

/** Max ancestor component fibers to climb when the host lands in node_modules. */
const MAX_CLIMB = 15;

/**
 * Build the production enricher. bippy is imported lazily because it touches
 * React DevTools internals and must never run during SSR. If bippy fails to
 * load the enricher degrades to a no-op (pins pass through unchanged).
 */
export function createBippyPinEnricher(capture?: ElementCapture): PinEnricher {
  return async (pins: Pin[]) => {
    try {
      const { getFiberFromHostInstance } = await import("bippy");
      const { getSource, getDisplayNameFromSource } = await import("bippy/source");
      const deps: PinSourceDeps = {
        findElement: (pin) => findPinElement(pin, capture),
        getFibers: (el) => collectFibers(el, getFiberFromHostInstance),
        getSource: (fiber) => getSource(fiber as never),
        getName: (fiber) => getDisplayNameFromSource(fiber as never),
      };
      return enrichPinsWithSource(pins, deps);
    } catch {
      return pins;
    }
  };
}

/** Host fiber, then ancestor component fibers (skipping intermediate host tags). */
function collectFibers(
  element: Element,
  getFiber: (el: Element) => { type?: unknown; return?: unknown } | null,
): unknown[] {
  const host = getFiber(element);
  if (!host) return [];
  const out: unknown[] = [host];
  let current = host.return as { type?: unknown; return?: unknown } | null | undefined;
  let climbed = 0;
  while (current && climbed < MAX_CLIMB) {
    if (typeof current.type !== "string") {
      out.push(current);
      climbed++;
    }
    current = current.return as { type?: unknown; return?: unknown } | null | undefined;
  }
  return out;
}

/**
 * The pinned element: the exact node captured at pick time (immune to selector
 * collisions and re-renders) if still connected, else a re-query by pinpoint's
 * stored domPath/selector as a fallback.
 */
function findPinElement(pin: Pin, capture?: ElementCapture): Element | null {
  const captured = capture?.elementFor(pin.id);
  if (captured && captured.isConnected) return captured;

  for (const query of [pin.element?.domPath, pin.element?.selector]) {
    if (!query) continue;
    try {
      const el = document.querySelector(query);
      if (el) return el;
    } catch {
      // invalid selector — try the next candidate
    }
  }
  return null;
}
